import { createContext, Fragment, PropsWithChildren, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { AnyObjectSchema } from 'yup';

import { IFormMetadataProps } from '@/components/Form/types';
import { useEnv } from '@/env/client';
import { useRecaptcha } from '@/hooks/useRecaptcha';
import { yupResolver } from '@hookform/resolvers/yup';
import { PropsWithClasses, PropsWithTags } from '@lamesarv-sdk/types';

interface CommonFormContextData {
  isDisabled?: boolean;
}

export const CommonFormContext = createContext({} as CommonFormContextData);

interface ICommonFormProps extends PropsWithChildren, PropsWithTags, PropsWithClasses {
  id?: string;
  isDisabled?: boolean;
  onSubmit: (data: unknown, metadata: Record<string, unknown>) => void;
  schema?: AnyObjectSchema;
  customCaptchaErrorMessage?: (message?: string) => JSX.Element;
  customErrorMessage?: (message?: string) => JSX.Element;
}

export const CommonForm = (props: ICommonFormProps) => {
  const [localDisabled, setLocalDisabled] = useState(false);
  const { env } = useEnv();
  const { hasError, verifyCaptcha } = useRecaptcha();

  const formMethods = useForm({
    resolver: props.schema ? yupResolver(props.schema) : undefined,
  });

  const contextData = useMemo(
    (): CommonFormContextData => ({ isDisabled: props.isDisabled || localDisabled }),
    [props.isDisabled, localDisabled],
  );

  const onSubmit = async (data: unknown) => {
    const metadata: Record<string, unknown> = {};

    if (env.ENABLE_FORMS_CAPTCHA) {
      setLocalDisabled(true);

      const response = await verifyCaptcha(env.RECAPTCHA_SITE_KEY, `submit__${props.id || 'common_form'}`);

      if (hasError || !response?.success) {
        formMethods.setError('captcha', {
          type: 'manual',
          message: response?.error || 'Captcha validation failed',
        });
        setLocalDisabled(false);
        return;
      }

      metadata.captchaScore = response?.score;
      metadata.captchaThreshold = response?.threshold;

      setLocalDisabled(false);
    }

    (data as IFormMetadataProps).source_url = window.location.href;
    props.onSubmit(data, metadata);
  };

  return (
    <CommonFormContext.Provider value={contextData}>
      <form
        id={props.id}
        noValidate
        onSubmit={formMethods.handleSubmit(onSubmit)}
        className={props.className}
        data-tag-group={props.tagGroup || 'page-components'}
        data-tag={props.tagItem ? `form-${props.tagItem}` : 'form'}
      >
        <FormProvider {...formMethods}>
          {props.children}
          {!!formMethods?.formState?.errors?.captcha && !props.customCaptchaErrorMessage && (
            <div className="fixed right-2 bottom-2 z-100 flex flex-row gap-1 items-center p-2 bg-red-200 text-red-700 border border-red-700 rounded-sm text-xs">
              <span>{String(formMethods?.formState?.errors?.captcha?.message)}...</span>
              <a
                href="#"
                className="underline underline-offset-2 decoration-dashed"
                onClick={() => window.location.reload()}
              >
                Refresh
              </a>
            </div>
          )}
          {!!formMethods?.formState?.errors?.captcha?.message && !!props.customCaptchaErrorMessage && (
            <Fragment>
              {props.customCaptchaErrorMessage(String(formMethods?.formState?.errors?.captcha?.message))}
            </Fragment>
          )}
          {!!props.customErrorMessage && (
            <Fragment>{props.customErrorMessage(String(formMethods?.formState?.errors?.captcha?.message))}</Fragment>
          )}
        </FormProvider>
      </form>
    </CommonFormContext.Provider>
  );
};
